import { useCallback, useEffect, useRef, useState } from "react";

/**
 * usePTT — real microphone capture using MediaRecorder.
 * start() begins recording; stop() returns { blob, base64, mimeType, durationMs }.
 * Also exposes `level` (0..1) for waveform meters via a WebAudio analyser.
 */
export function usePTT() {
  const [recording, setRecording] = useState(false);
  const [level, setLevel] = useState(0);
  const [error, setError] = useState(null);

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAtRef = useRef(0);
  const analyserRef = useRef(null);
  const rafRef = useRef(0);
  const audioCtxRef = useRef(null);

  const cleanup = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch (_) {}
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
    setLevel(0);
  }, []);

  const startMeter = (stream) => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      analyserRef.current = analyser;
      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          const v = (data[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / data.length);
        setLevel(Math.min(1, rms * 2.5));
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (e) { /* meter is optional */ }
  };

  const start = useCallback(async (deviceId = "") => {
    setError(null);
    try {
      const constraints = deviceId ? { audio: { deviceId: { exact: deviceId } } } : { audio: true };
      const stream = await navigator.mediaDevices.getUserMedia(constraints).catch(async (e) => {
        if (deviceId) return navigator.mediaDevices.getUserMedia({ audio: true });
        throw e;
      });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.start();
      mediaRecorderRef.current = rec;
      startedAtRef.current = Date.now();
      setRecording(true);
      startMeter(stream);
      return true;
    } catch (e) {
      setError(e.message || "Microphone access denied");
      cleanup();
      return false;
    }
  }, [cleanup]);

  const stop = useCallback(() => {
    return new Promise((resolve) => {
      const rec = mediaRecorderRef.current;
      if (!rec) { cleanup(); setRecording(false); resolve(null); return; }
      rec.onstop = async () => {
        const durationMs = Date.now() - startedAtRef.current;
        const blob = new Blob(chunksRef.current, { type: rec.mimeType });
        const arrayBuf = await blob.arrayBuffer();
        // convert to base64 (safe for larger sizes)
        const bytes = new Uint8Array(arrayBuf);
        let binary = "";
        const chunk = 0x8000;
        for (let i = 0; i < bytes.length; i += chunk) {
          binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
        }
        const base64 = btoa(binary);
        cleanup();
        setRecording(false);
        resolve({ blob, base64, mimeType: rec.mimeType, durationMs });
      };
      rec.stop();
    });
  }, [cleanup]);

  useEffect(() => () => cleanup(), [cleanup]);

  return { recording, level, error, start, stop };
}
